import React, { useEffect, useState } from "react";
import panda from "../../../../../../assets/PandaProfile.png";
import GlassPinboard from "./components/GlassPinboard";
import StickyNote from "./StickyNote";


const VALUES = [
  "Service",
  "Supportiveness",
  "Tolerance",
  "Transparency",
  "Wisdom",
  "Purpose",
  "Financial responsibility",
  "Personal development",
  "Initiative",
  "Justice",
  "Forgiveness",
  "Resourcefulness",
  "Security",
  "Safety",
  "Stewardship",
  "Graciousness",
  "Gratitude",
  "Responsibility",
  "Assertiveness",
  "Accountability",
  "Risk-taking",
  "Self-expression",
  "Achievement",
  "Accomplishment",
  "Hard work",
  "Connection",
  "Contribution",
];


const CONTAINER_HEIGHT = 526;
const CONTAINER_WIDTH = 385;


const ValuesSelectionScreen = ({ onFinish, onToggleImmersive, initialSelected = [], isSaving = false }) => {
  const [selected, setSelected] = useState(initialSelected);
  const [hovered, setHovered] = useState(null);


  // Load initial selected values on mount
  useEffect(() => {
    if (initialSelected && initialSelected.length > 0) {
      setSelected(initialSelected);
    }
  }, [initialSelected]);


  useEffect(() => {
    if (typeof onToggleImmersive === "function") {
      onToggleImmersive(true);
    }
    return () => {
      if (typeof onToggleImmersive === "function") {
        onToggleImmersive(false);
      }
    };
  }, [onToggleImmersive]);


  const handleToggle = (val) => {
    setSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };


  const getPosition = (index) => {
    if (index < 12) {
      const col = Math.floor(index / 3);
      const row = index % 3;
      return {
        top: 65 + row * 140,
        left: 50 + col * 140,
        colorIndex: col,
        zIndex: 1,
      };
    } else if (index < 18) {
      const offsetIndex = index - 12;
      const col = Math.floor(offsetIndex / 2);
      const row = offsetIndex % 2;
      const offsetColorMap = [2, 3, 0];
      return {
        top: 75 + row * 140 + 60,
        left: 40 + col * 150 + 80,
        colorIndex: offsetColorMap[col % 3],
        zIndex: 2,
      };
    } else {
      return null;
    }
  };


  return (
    <div className="w-full flex flex-col items-start justify-start pt-6 pb-16">
      {/* Saving indicator */}
      {isSaving && (
        <div className="fixed top-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 z-50">
          <span className="text-xs text-gray-600 font-gotham animate-pulse flex items-center gap-2">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        </div>
      )}


      {/* Panda and Heading */}
      <div className="flex mt-4 items-center gap-4 mb-2 pl-2">
        <img
          src={panda}
          alt="Panda"
          style={{ width: 100.64, height: 100.64 }}
          draggable={false}
        />
        <div>
          <h2
            style={{
              fontFamily: "Gotham, sans-serif",
              fontWeight: 500,
              fontSize: 28,
              color: "#232323",
              lineHeight: "1.35",
            }}
            className="mb-1 ml-4"
          >
            Currently, what are the values that matter for you?
          </h2>
          <p
            style={{
              fontFamily: "Gotham, sans-serif",
              fontWeight: 400,
              fontSize: 15,
              color: "#888888",
              lineHeight: "1.65",
            }}
            className="ml-4"
          >
            Choose as many as you want from the list below
          </p>
        </div>
      </div>


      {/* Values List & Pinboard Side by Side */}
      <div className="flex flex-row ml-36 items-start gap-10 mt-2">
        {/* List of Values */}
        <div>
          <div
            style={{
              fontFamily: "Gotham, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "1.35",
              textTransform: "uppercase",
              color: "#323232",
              marginBottom: 10,
            }}
          >
            LIST OF VALUES
          </div>
          <div
            className="custom-scrollbar-transparent"
            style={{
              width: CONTAINER_WIDTH,
              height: CONTAINER_HEIGHT,
              borderRadius: 10,
              border: "1px solid #CCCCCC",
              overflowY: "auto",
              background: "#fff",
            }}
          >
            <ul className="px-2">
              {VALUES.map((val) => {
                const isSelected = selected.includes(val);
                const isHovered = hovered === val;
                
                return (
                  <li
                    key={val}
                    className={`
                      px-6 py-3 cursor-pointer select-none transition flex items-center justify-between text-[#323232]
                      ${isHovered ? "bg-[#DAF1F1]" : ""}
                    `}
                    style={{
                      fontFamily: "Gotham, sans-serif",
                      fontSize: 16,  // ✅ REDUCED from 18px to 16px
                    }}
                    onMouseEnter={() => setHovered(val)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => handleToggle(val)}
                  >
                    <span>{val}</span>
                    
                    {/* ✅ SHOW MINUS (-) IF SELECTED, PLUS (+) IF HOVERED */}
                    {isSelected ? (
                      <span
                        style={{
                          color: "#54B0AF",  // Red color for minus
                          fontSize: 24,
                          fontWeight: 500,
                          marginLeft: 10,
                          lineHeight: 1,
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                      >
                        −
                      </span>
                    ) : isHovered ? (
                      <span
                        style={{
                          color: "#54B0AF",  // Teal color for plus
                          fontSize: 24,
                          fontWeight: 500,
                          marginLeft: 10,
                          lineHeight: 1,
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                      >
                        +
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>


        {/* Glass Pinboard with overlay sticky notes */}
        <GlassPinboard className="mt-8">
          {/* Show "Select Value" placeholder when no values selected */}
          {selected.length === 0 && (
            <StickyNote
              text="Select Value"
              style={{ top: 10, left: 10 }}
              colorIndex={0}
              onClick={() => {}}
            />
          )}


          {/* Display selected values as sticky notes - LIMIT TO 18 */}
          {selected.slice(0, 18).map((value, index) => {
            const position = getPosition(index);
            if (!position) return null;


            return (
              <StickyNote
                key={value}
                text={value}
                style={{
                  top: position.top,
                  left: position.left,
                  zIndex: position.zIndex,
                }}
                colorIndex={position.colorIndex}
                rotation={Math.random() * 6 - 3}
                onClick={() => handleToggle(value)}
              />
            );
          })}
        </GlassPinboard>
      </div>


      {/* Bottom Button */}
      <div className="mt-6 ml-36">
        <button
          onClick={() => onFinish?.(selected)}
          disabled={selected.length === 0}
          style={{
            width: 284,
            height: 69,
            borderRadius: 20,
            background: selected.length > 0 ? "#54B0AF" : "#CCCCCC",
            paddingTop: 22,
            paddingBottom: 22,
            paddingLeft: 20,
            paddingRight: 20,
            fontFamily: "Gotham, sans-serif",
            cursor: selected.length > 0 ? "pointer" : "not-allowed",
          }}
          className="flex items-center justify-between text-white font-medium text-[21px] shadow hover:opacity-90 transition"
        >
          <span className="font-gotham font-medium">Finish Selection</span>
          <span className="ml-4 flex items-center justify-center">
            <span
              className="flex items-center justify-center"
              style={{
                background: "#fff",
                borderRadius: "50%",
                width: 20,
                height: 20,
              }}
            >
              <svg width="18" height="18" fill="#54B0AF" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};


export default ValuesSelectionScreen;


// import React, { useEffect, useState } from "react";
// import panda from "../../../../../../assets/PandaProfile.png";
// import GlassPinboard from "./components/GlassPinboard";
// import StickyNote from "./StickyNote";

// const VALUES = [
//   "Service",
//   "Supportiveness",
//   "Tolerance",
//   "Transparency",
//   "Wisdom",
//   "Purpose",
//   "Financial responsibility",
//   "Personal development",
//   "Initiative",
//   "Justice",
//   "Forgiveness",
//   "Resourcefulness",
//   "Security",
//   "Safety",
//   "Stewardship",
//   "Graciousness",
//   "Gratitude",
//   "Responsibility",
//   "Assertiveness",
//   "Accountability",
//   "Risk-taking",
//   "Self-expression",
//   "Achievement",
//   "Accomplishment",
//   "Hard work",
//   "Connection",
//   "Contribution",
// ];

// const CONTAINER_HEIGHT = 526;
// const CONTAINER_WIDTH = 385;

// const ValuesSelectionScreen = ({ onFinish, onToggleImmersive, initialSelected = [], isSaving = false }) => {
//   const [selected, setSelected] = useState(initialSelected);
//   const [hovered, setHovered] = useState(null);

//   // Load initial selected values on mount
//   useEffect(() => {
//     if (initialSelected && initialSelected.length > 0) {
//       setSelected(initialSelected);
//     }
//   }, [initialSelected]);

//   useEffect(() => {
//     if (typeof onToggleImmersive === "function") {
//       onToggleImmersive(true);
//     }
//     return () => {
//       if (typeof onToggleImmersive === "function") {
//         onToggleImmersive(false);
//       }
//     };
//   }, [onToggleImmersive]);

//   const handleToggle = (val) => {
//     setSelected((prev) =>
//       prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
//     );
//   };

//   const getPosition = (index) => {
//     if (index < 12) {
//       const col = Math.floor(index / 3);
//       const row = index % 3;
//       return {
//         top: 65 + row * 140,
//         left: 50 + col * 140,
//         colorIndex: col,
//         zIndex: 1,
//       };
//     } else if (index < 18) {
//       const offsetIndex = index - 12;
//       const col = Math.floor(offsetIndex / 2);
//       const row = offsetIndex % 2;
//       const offsetColorMap = [2, 3, 0];
//       return {
//         top: 75 + row * 140 + 60,
//         left: 40 + col * 150 + 80,
//         colorIndex: offsetColorMap[col % 3],
//         zIndex: 2,
//       };
//     } else {
//       return null;
//     }
//   };

//   return (
//     <div className="w-full flex flex-col items-start justify-start pt-6 pb-16">
//       {/* Saving indicator */}
//       {isSaving && (
//         <div className="fixed top-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 z-50">
//           <span className="text-xs text-gray-600 font-gotham animate-pulse flex items-center gap-2">
//             <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
//               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//             </svg>
//             Saving...
//           </span>
//         </div>
//       )}

//       {/* Panda and Heading */}
//       <div className="flex mt-4 items-center gap-4 mb-2 pl-2">
//         <img
//           src={panda}
//           alt="Panda"
//           style={{ width: 100.64, height: 100.64 }}
//           draggable={false}
//         />
//         <div>
//           <h2
//             style={{
//               fontFamily: "Gotham, sans-serif",
//               fontWeight: 500,
//               fontSize: 28,
//               color: "#232323",
//               lineHeight: "1.35",
//             }}
//             className="mb-1 ml-4"
//           >
//             Currently, what are the values that matter for you?
//           </h2>
//           <p
//             style={{
//               fontFamily: "Gotham, sans-serif",
//               fontWeight: 400,
//               fontSize: 15,
//               color: "#888888",
//               lineHeight: "1.65",
//             }}
//             className="ml-4"
//           >
//             Choose as many as you want from the list below
//           </p>
//         </div>
//       </div>

//       {/* Values List & Pinboard Side by Side */}
//       <div className="flex flex-row ml-36 items-start gap-10 mt-2">
//         {/* List of Values */}
//         <div>
//           <div
//             style={{
//               fontFamily: "Gotham, sans-serif",
//               fontWeight: 500,
//               fontSize: 14,
//               lineHeight: "1.35",
//               textTransform: "uppercase",
//               color: "#323232",
//               marginBottom: 10,
//             }}
//           >
//             LIST OF VALUES
//           </div>
//           <div
//             className="custom-scrollbar-transparent"
//             style={{
//               width: CONTAINER_WIDTH,
//               height: CONTAINER_HEIGHT,
//               borderRadius: 10,
//               border: "1px solid #CCCCCC",
//               overflowY: "auto",
//               background: "#fff",
//             }}
//           >
//             <ul className="px-2">
//               {VALUES.map((val) => (
//                 <li
//                   key={val}
//                   className={`
//                     px-6 py-3 cursor-pointer select-none text-[18px] transition flex items-center justify-between text-[#323232]
//                     ${hovered === val ? "bg-[#DAF1F1]" : ""}
//                   `}
//                   style={{
//                     fontFamily: "Gotham, sans-serif",
//                   }}
//                   onMouseEnter={() => setHovered(val)}
//                   onMouseLeave={() => setHovered(null)}
//                   onClick={() => handleToggle(val)}
//                 >
//                   <span>{val}</span>
//                   {hovered === val && (
//                     <span
//                       style={{
//                         color: "#222",
//                         fontSize: 24,
//                         fontWeight: 500,
//                         marginLeft: 10,
//                         lineHeight: 1,
//                         userSelect: "none",
//                         pointerEvents: "none",
//                       }}
//                     >
//                       +
//                     </span>
//                   )}
//                 </li>
//               ))}
//             </ul>
//           </div>
//         </div>

//         {/* Glass Pinboard with overlay sticky notes */}
//         <GlassPinboard className="mt-8">
//           {/* Show "Select Value" placeholder when no values selected */}
//           {selected.length === 0 && (
//             <StickyNote
//               text="Select Value"
//               style={{ top: 10, left: 10 }}
//               colorIndex={0}
//               onClick={() => {}}
//             />
//           )}

//           {/* Display selected values as sticky notes - LIMIT TO 18 */}
//           {selected.slice(0, 18).map((value, index) => {
//             const position = getPosition(index);
//             if (!position) return null;

//             return (
//               <StickyNote
//                 key={value}
//                 text={value}
//                 style={{
//                   top: position.top,
//                   left: position.left,
//                   zIndex: position.zIndex,
//                 }}
//                 colorIndex={position.colorIndex}
//                 rotation={Math.random() * 6 - 3}
//                 onClick={() => handleToggle(value)}
//               />
//             );
//           })}
//         </GlassPinboard>
//       </div>

//       {/* Bottom Button */}
//       <div className="mt-6 ml-36">
//         <button
//           onClick={() => onFinish?.(selected)}
//           disabled={selected.length === 0}
//           style={{
//             width: 284,
//             height: 69,
//             borderRadius: 20,
//             background: selected.length > 0 ? "#54B0AF" : "#CCCCCC",
//             paddingTop: 22,
//             paddingBottom: 22,
//             paddingLeft: 20,
//             paddingRight: 20,
//             fontFamily: "Gotham, sans-serif",
//             cursor: selected.length > 0 ? "pointer" : "not-allowed",
//           }}
//           className="flex items-center justify-between text-white font-medium text-[21px] shadow hover:opacity-90 transition"
//         >
//           <span className="font-gotham font-medium">Finish Selection</span>
//           <span className="ml-4 flex items-center justify-center">
//             <span
//               className="flex items-center justify-center"
//               style={{
//                 background: "#fff",
//                 borderRadius: "50%",
//                 width: 20,
//                 height: 20,
//               }}
//             >
//               <svg width="18" height="18" fill="#54B0AF" viewBox="0 0 20 20">
//                 <path
//                   fillRule="evenodd"
//                   d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
//                   clipRule="evenodd"
//                 />
//               </svg>
//             </span>
//           </span>
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ValuesSelectionScreen;
